import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useEffect } from 'react';
import styled from 'styled-components';

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
}

const Wrapper = styled.div`
  border: 1px solid #edf2f7;
  border-radius: 10px;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
  background: #fff;
  overflow: hidden;

  &:focus-within {
    border-color: #ff8e8e;
    box-shadow: 0 0 0 2px rgba(255, 142, 142, 0.15);
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 10px;
  border-bottom: 1px solid #edf2f7;
  background: #f9fafb;
`;

const ToolbarButton = styled.button<{ $active?: boolean }>`
  padding: 4px 10px;
  border: 1px solid ${({ $active }) => ($active ? '#ff8e8e' : '#edf2f7')};
  border-radius: 6px;
  background: ${({ $active }) => ($active ? 'rgba(255,142,142,0.1)' : '#fff')};
  color: ${({ $active }) => ($active ? '#ff6b6b' : '#64748b')};
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;
  line-height: 1.4;
  transition: all 0.15s;

  &:hover {
    background: rgba(255, 142, 142, 0.1);
    border-color: #ff8e8e;
    color: #ff6b6b;
  }
`;

const Divider = styled.div`
  width: 1px;
  background: #edf2f7;
  margin: 2px 2px;
  align-self: stretch;
`;

const EditorArea = styled.div`
  .tiptap {
    padding: 12px 14px;
    min-height: 220px;
    outline: none;
    font-size: 14px;
    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      sans-serif;
    line-height: 1.7;
    color: #1a1a1a;

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      color: #c0c8d0;
      pointer-events: none;
      float: left;
      height: 0;
    }

    h2 {
      font-size: 16px;
      font-weight: 700;
      margin: 14px 0 6px;
      color: #1a1a1a;
    }

    h3 {
      font-size: 14px;
      font-weight: 700;
      margin: 10px 0 4px;
      color: #1a1a1a;
    }

    ul,
    ol {
      padding-left: 22px;
      margin: 6px 0;
    }

    li {
      margin: 2px 0;
    }

    p {
      margin: 4px 0;
    }

    strong {
      font-weight: 700;
    }

    em {
      font-style: italic;
      color: #64748b;
    }
  }
`;

export const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [StarterKit, Placeholder.configure({ placeholder: 'Введіть опис товару...' })],
    content: value ?? '',
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (current !== value) {
      editor.commands.setContent(value ?? '', { emitUpdate: false });
    }
  }, [value, editor]);

  return (
    <Wrapper>
      <Toolbar onMouseDown={(e) => e.preventDefault()}>
        <ToolbarButton
          $active={editor?.isActive('bold')}
          onClick={() => editor?.chain().focus().toggleBold().run()}
          title="Жирний"
        >
          B
        </ToolbarButton>
        <ToolbarButton
          $active={editor?.isActive('italic')}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          title="Курсив"
          style={{ fontStyle: 'italic' }}
        >
          I
        </ToolbarButton>
        <Divider />
        <ToolbarButton
          $active={editor?.isActive('heading', { level: 2 })}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          $active={editor?.isActive('heading', { level: 3 })}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          H3
        </ToolbarButton>
        <Divider />
        <ToolbarButton
          $active={editor?.isActive('bulletList')}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          • Список
        </ToolbarButton>
        <ToolbarButton
          $active={editor?.isActive('orderedList')}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        >
          1. Список
        </ToolbarButton>
      </Toolbar>
      <EditorArea>
        <EditorContent editor={editor} />
      </EditorArea>
    </Wrapper>
  );
};
